import { Component, ChangeDetectionStrategy, Signal, signal, inject } from '@angular/core';
import { Store } from '@ngrx/store';
import { map, Observable, combineLatest } from 'rxjs';
import {
  selectBackgroundNodesList, selectIn3dView, selectInitiallySelectedBackgroundNodes, selectLayersWithoutWebMercatorIds,
  selectSelectedBackgroundNodeId,
} from '../../map/state/map.selectors';
import { SplitButtonOptionModel, SplitButtonComponent } from '@tailormap-viewer/shared';
import { setSelectedBackgroundNodeId } from '../../map/state/map.actions';
import { AsyncPipe } from '@angular/common';

@Component({
    selector: 'tm-background-layer-toggle',
    templateUrl: './background-layer-toggle.component.html',
    styleUrls: ['./background-layer-toggle.component.css'],
    changeDetection: ChangeDetectionStrategy.OnPush,
    imports: [ SplitButtonComponent, AsyncPipe ],
})
export class BackgroundLayerToggleComponent {
  private store$ = inject(Store);

  public selectedBackgroundNodeId$: Observable<string | undefined>;
  public backgroundLayers$: Observable<SplitButtonOptionModel[]>;
  public initiallyCheckedLabels$: Observable<string>;

  private allowEmptyBackground = true;

  public in3D: Signal<boolean> = signal(false);
  public layersWithoutWebMercator: Signal<string[]> = signal([]);

  constructor() {
    this.selectedBackgroundNodeId$ = combineLatest([
      this.store$.select(selectInitiallySelectedBackgroundNodes),
      this.store$.select(selectSelectedBackgroundNodeId),
    ]).pipe(
      map(([ initiallySelectedBackgroundNodes, selectedBackgroundNodeId ]) => {
        return selectedBackgroundNodeId ||
          (initiallySelectedBackgroundNodes.length === 1
            ? initiallySelectedBackgroundNodes[0].id
            : undefined);
      }));
    this.backgroundLayers$ = this.store$.select(selectBackgroundNodesList).pipe(
      map(nodes => this.getEmptyOption().concat([
        ...nodes.map(node => ({ id: node.id, label: node.name })),
      ])),
    );
    this.initiallyCheckedLabels$ = this.store$.select(selectInitiallySelectedBackgroundNodes).pipe(
      map(nodes => nodes.map(node => node.name).join(', ')),
    );
    this.in3D = this.store$.selectSignal(selectIn3dView);
    this.layersWithoutWebMercator = this.store$.selectSignal(selectLayersWithoutWebMercatorIds);
  }

  public backgroundChanged(id: string) {
    this.store$.dispatch(setSelectedBackgroundNodeId({ id }));
  }

  private getEmptyOption(): SplitButtonOptionModel[] {
    if (!this.allowEmptyBackground) {
      return [];
    }
    return [{ id: 'EMPTY_BACKGROUND', label: $localize `:@@core.background-layer-toggle.no-basemap:No basemap` }];
  }

}
